import { useEffect, useRef, useState, useCallback } from 'react'


type StatsData = {
  carry: {
    positions: unknown[]
    stats: unknown
  }
  autoExecute: {
    running: boolean
    startedAt: string | null
    scansCompleted: number
    hedgesFound: number
    entriesFound: number
    tradesExecuted: number
    tradesSkipped: number
    totalInvested: number
    lastScanAt: string | null
    lastTradeAt: string | null
    errors: number
    lastError: string | null
  }
  redemption?: {
    running: boolean
    startedAt: string | null
    scansCompleted: number
    positionsRedeemed: number
    totalAmountReceived: number
    lastScanAt: string | null
    lastRedemptionAt: string | null
    errors: number
    lastError: string | null
  }
  db: {
    totalPositions: number
    activePositions: number
  }
  timestamp: number
}

type WSMessage = 
  | { type: 'stats', data: StatsData }
  | { type: 'positions_updated', data: unknown }
  | { type: 'positions_cleared', data: unknown }
  | { type: 'trade_executed', data: unknown }
  | { type: 'connected', subscriptions: string[] }
  | { type: 'subscribed', channels: string[] }
  | { type: 'unsubscribed', channels: string[] }
  | { type: 'pong' }

export function useWebSocket(url: string) {
  const [connected, setConnected] = useState(false)
  const [stats, setStats] = useState<StatsData | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 5

  // Helper function to handle connection logic
  const connectInternal = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    console.log('[WS] Connecting to', url)
    const ws = new WebSocket(url)

    ws.onopen = () => {
      console.log('[WS] Connected')
      setConnected(true)
      reconnectAttemptsRef.current = 0
    }

    ws.onmessage = (event) => {
      try {
        const message: WSMessage = JSON.parse(event.data)
        
        switch (message.type) {
          case 'stats':
            setStats(message.data)
            break
            
          case 'positions_updated':
          case 'positions_cleared':
            console.log('[WS] Event:', message.type, message.data)
            break
            
          case 'connected':
            console.log('[WS] Subscribed to:', message.subscriptions)
            break
            
          case 'subscribed':
            console.log('[WS] Subscribed to channels:', message.channels)
            break
            
          case 'unsubscribed':
            console.log('[WS] Unsubscribed from channels:', message.channels)
            break
        }
      } catch (err) {
        console.error('[WS] Failed to parse message:', err)
      }
    }

    ws.onerror = (error) => {
      console.error('[WS] Error:', error)
    }

    ws.onclose = () => {
      console.log('[WS] Disconnected')
      setConnected(false)
      wsRef.current = null

      // Exponential backoff reconnect
      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000)
        console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`)
        
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++
          // Recursively call connect via the ref to avoid dependency cycles or used-before-declaration
          connectRef.current()
        }, delay)
      } else {
        console.error('[WS] Max reconnect attempts reached. Falling back to polling.')
      }
    }

    wsRef.current = ws
  }, [url])

  // Ref to hold the current version of connectInternal
  const connectRef = useRef(connectInternal)
  
  // Update ref when connectInternal changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability
    connectRef.current = connectInternal
  }, [connectInternal])


  // Public connect function that uses the ref
  const connect = useCallback(() => {
    connectRef.current?.()
  }, [])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
  }, [])

  const subscribe = useCallback((channels: string[]) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', channels }))
    }
  }, [])

  const unsubscribe = useCallback((channels: string[]) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channels }))
    }
  }, [])

  const ping = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }))
    }
  }, [])

  useEffect(() => {
    connect()
    
    // Send ping every 30 seconds to keep connection alive
    const pingInterval = setInterval(ping, 30000)
    
    return () => {
      clearInterval(pingInterval)
      disconnect()
    }
  }, [connect, disconnect, ping])

  return { connected, stats, subscribe, unsubscribe, ping }
}
